import React, { useEffect, useState } from "react";
import Loading from "../../../../common-components/Loading";
import { getLaborUtilizationDetailsAPI } from "../../../../api/reports-api";
import PaginationBar, { usePagination, PAGE_SIZE_OPTIONS } from "./Pagination";
import { useLuTableSortFilter, LuSortFilterTh } from "./tableSortFilterUtils";

const COLS = [
  { key: "empId", label: "Emp Id", isText: true },
  { key: "empName", label: "Emp Name", isText: true },
  { key: "projectId", label: "Project Id", isText: true },
  { key: "projectName", label: "Project Name", isText: true },
  { key: "notes", label: "Notes", isText: true },
  { key: "year", label: "Year", isText: true },
  { key: "monthName", label: "MonthName", isText: true },
  { key: "hours", label: "Hours", isText: false },
  { key: "stdHours", label: "Standard Hours", isText: false },
  { key: "utilPct", label: "Utilization %", isText: false },
];

function mapDetailRow(r) {
  const util =
    r.utilizationPct != null
      ? Number(r.utilizationPct)
      : r.utilPct != null
        ? Number(r.utilPct)
        : null;
  return {
    empId: r.empId ?? "",
    empName: r.empName ?? "",
    projectId: r.projectId ?? "",
    projectName: r.projectName ?? "",
    notes: r.notes ?? "",
    year: r.year ?? "",
    monthName: r.monthName ?? "",
    hours: r.hours ?? null,
    stdHours: r.standardHours ?? r.stdHours ?? null,
    // API returns a ratio (e.g. 0.9474); store as percent for display/sort/filter
    utilPct: util == null || Number.isNaN(util) ? null : util * 100,
  };
}

export default function DetailsTab({ classes, filters, onMeta }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const {
    sortKeys,
    sortedRows,
    openFilterKey,
    onThClick,
    getSortColumnLabel,
    hasFilterValue,
    toggleFilter,
    filterBtnRefs,
    renderFilterPanel,
  } = useLuTableSortFilter(COLS, rows);

  const { pagedItems: pagedRows, resetPage, ...pagination } = usePagination(
    sortedRows,
    PAGE_SIZE_OPTIONS[0],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLaborUtilizationDetailsAPI(filters)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data;
        if (d) {
          setRows((d.rows || []).map(mapDetailRow));
          resetPage();
          onMeta?.({ actualThroughMonth: d.actualThroughMonth, fiscalYear: d.fiscalYear });
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  const NEG_COLOR = "#DC2626";

  const fmtNum = (v, dec = 2) => {
    if (v == null || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    const abs = Math.abs(n).toLocaleString("en-US", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    });
    return n < 0 ? `(${abs})` : abs;
  };

  const fmtPct = (v) => {
    if (v == null || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    const abs = Math.abs(n).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return n < 0 ? `(${abs}%)` : `${abs}%`;
  };

  const negStyle = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n < 0 ? { color: NEG_COLOR } : undefined;
  };

  if (loading) return <Loading />;

  const thProps = {
    sortKeys,
    onThClick,
    getSortColumnLabel,
    openFilterKey,
    hasFilterValue,
    toggleFilter,
    filterBtnRefs,
  };

  return (
    <div>
      <div className={classes.tableWrapScrollable}>
        <table className={classes.table}>
          <thead>
            <tr>
              {COLS.map((col) => (
                <LuSortFilterTh
                  key={col.key}
                  col={col}
                  className={col.isText ? classes.thDetail : classes.thDetailNum}
                  {...thProps}
                  align={col.isText ? "left" : "right"}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, i) => {
              const stripe = { background: i % 2 === 0 ? "#fff" : "#F4F6F9" };
              return (
                <tr key={`${row.empId}-${row.projectId}-${row.monthName}-${i}`}>
                  <td className={classes.tdDetailText} style={stripe}>{row.empId}</td>
                  <td className={classes.tdDetailWrap} style={stripe}>{row.empName}</td>
                  <td className={classes.tdDetailText} style={stripe}>{row.projectId}</td>
                  <td className={classes.tdDetailText} style={stripe}>{row.projectName}</td>
                  <td className={classes.tdDetailText} style={stripe}>{row.notes}</td>
                  <td className={classes.tdDetailText} style={stripe}>{row.year}</td>
                  <td className={classes.tdDetailText} style={stripe}>{row.monthName}</td>
                  <td className={classes.tdDetailNum} style={{ ...stripe, ...negStyle(row.hours) }}>{fmtNum(row.hours)}</td>
                  <td className={classes.tdDetailNum} style={{ ...stripe, ...negStyle(row.stdHours) }}>{fmtNum(row.stdHours, 0)}</td>
                  <td className={classes.tdDetailNum} style={{ ...stripe, ...negStyle(row.utilPct) }}>{fmtPct(row.utilPct)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <PaginationBar
        {...pagination}
        totalItems={sortedRows.length}
        onPageChange={pagination.goToPage}
        onPageSizeChange={pagination.handlePageSizeChange}
      />
      {renderFilterPanel()}
    </div>
  );
}
